"use client";

import { Check, Minus, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { BuildingCompanyProfileProgress } from "~/components/siteglue/siteglue-extended-knowledge-panel";
import type { SiteGlueConsultantState } from "~/lib/siteglue/consultant-onboarding";
import { createEmptyConsultantState } from "~/lib/siteglue/consultant-onboarding";
import {
  AI_EMPLOYEE_FEATURE_SECTIONS,
  AI_EMPLOYEE_PLANS,
  type AiEmployeeFeature,
  type AiEmployeePlanId,
  type FeatureValue,
} from "~/lib/siteglue/ai-employee-plans";
import { cn } from "~/lib/utils";

type SiteGluePlanPricingPanelProps = {
  state: SiteGlueConsultantState | null;
  className?: string;
  onSelectPlan?: (planId: Exclude<AiEmployeePlanId, "enterprise">) => void;
  /** When true, show company-profile build spinner instead of plan cards. */
  isBuilding?: boolean;
};

/** Hire Pricing tab CTAs — require Sign Up (Intern) or purchase (paid). */
function planActionLabel(planId: Exclude<AiEmployeePlanId, "enterprise">): string {
  return AI_EMPLOYEE_PLANS.find((plan) => plan.id === planId)?.ctaLabel ?? "Start 15-day trial";
}

const DISPLAY_PLANS = ["intern", "associate", "professional"] as const;

/** Always-visible Usage block only — profile/channels/skills live in the accordion list. */
const CARD_TOP_SECTIONS = ["usage"] as const;

const CARD_FEATURE_SECTIONS = [
  "ai_employee",
  "channels",
  "skills",
  "knowledge",
  "organization",
] as const;

const CARD_SECTION_TITLES: Record<(typeof CARD_FEATURE_SECTIONS)[number], string> = {
  ai_employee: "My Profile",
  channels: "My Channels",
  skills: "My Skills",
  knowledge: "My Training",
  organization: "My Organization",
};

/** Only a user-chosen selectedPlan counts — never default to Intern. */
function selectedPlanFromState(state: SiteGlueConsultantState): AiEmployeePlanId | null {
  return state.selectedPlan ?? null;
}

function FeatureValueMark({ value }: { value: FeatureValue }) {
  if (value === false) {
    return (
      <span className="inline-flex text-slate-300" aria-label="Not included">
        <Minus className="h-4 w-4" aria-hidden />
      </span>
    );
  }
  if (value === true) {
    return (
      <span className="inline-flex text-[#4f81bd]" aria-label="Included">
        <Check className="h-4 w-4 stroke-[2.5]" aria-hidden />
      </span>
    );
  }
  return <span className="text-xs font-medium text-slate-700">{value}</span>;
}

function FeatureRow({
  feature,
  planId,
  pricingStyle = false,
}: {
  feature: AiEmployeeFeature;
  planId: AiEmployeePlanId;
  pricingStyle?: boolean;
}) {
  const value = feature.values[planId];
  const included = value === true || (typeof value === "string" && value.trim().length > 0);
  const detail =
    typeof value === "string" && value.trim() && value.trim().toLowerCase() !== "true"
      ? value.trim()
      : null;

  if (pricingStyle) {
    return (
      <li className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
        <span className="min-w-0 font-medium text-slate-800">{feature.label}</span>
        <FeatureValueMark value={value} />
      </li>
    );
  }

  return (
    <li className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-700">
      {included ? (
        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="Included" />
      ) : (
        <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" aria-label="Not included" />
      )}
      <span className="min-w-0 font-medium">
        {feature.label}
        {detail ? (
          <span className="font-normal text-slate-500"> · {detail}</span>
        ) : null}
      </span>
    </li>
  );
}

function PlanSectionBlock({
  title,
  features,
  planId,
}: {
  title: string;
  features: AiEmployeeFeature[];
  planId: AiEmployeePlanId;
}) {
  return (
    <div>
      <div className="border-y border-slate-200 bg-slate-50/90 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {title}
      </div>
      <ul className="divide-y divide-slate-100">
        {features.map((feature) => (
          <FeatureRow key={feature.id} feature={feature} planId={planId} pricingStyle />
        ))}
      </ul>
    </div>
  );
}

function PlanEmployeeCard({
  planId,
  selected,
  onSelect,
}: {
  planId: Exclude<AiEmployeePlanId, "enterprise">;
  selected: boolean;
  onSelect?: () => void;
}) {
  const plan = AI_EMPLOYEE_PLANS.find((p) => p.id === planId);
  if (!plan) return null;

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col rounded-2xl border bg-white text-left shadow-sm transition-colors",
        selected
          ? "border-[#4f81bd] ring-2 ring-[#4f81bd]/20"
          : "border-slate-200 hover:border-slate-300",
      )}
    >
      <div className="space-y-2 border-b border-slate-100 px-4 pb-4 pt-5 text-center">
        <div className="space-y-1">
          <div className="text-base font-semibold text-slate-900">{plan.name}</div>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-3xl font-bold tracking-tight text-slate-900">
              {plan.priceLabel}
            </span>
            {plan.priceSubLabel ? (
              <span className="text-xs font-medium text-slate-500">{plan.priceSubLabel}</span>
            ) : null}
          </div>
          <p className="mx-auto max-w-[14rem] text-xs leading-snug text-slate-500">
            {plan.tagline}
          </p>
          {onSelect ? (
            <div className="flex justify-center pt-1">
              <Button
                type="button"
                variant={selected || planId === "intern" ? "default" : "outline"}
                size="sm"
                className={cn(
                  "min-w-[8.5rem]",
                  (selected || planId === "intern") &&
                    "bg-[#4f81bd] hover:bg-[#4f81bd]/90",
                )}
                onClick={onSelect}
              >
                {planActionLabel(planId)}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-0">
        {CARD_TOP_SECTIONS.map((sectionId) => {
          const section = AI_EMPLOYEE_FEATURE_SECTIONS.find((s) => s.id === sectionId);
          if (!section) return null;
          return (
            <PlanSectionBlock
              key={section.id}
              title={section.title}
              features={section.features}
              planId={planId}
            />
          );
        })}
      </div>

      <div className="min-h-0 flex-1 px-4 pt-1">
        <Accordion
          type="multiple"
          className="w-full"
          defaultValue={[...CARD_FEATURE_SECTIONS]}
        >
          {CARD_FEATURE_SECTIONS.map((sectionId) => {
            const section = AI_EMPLOYEE_FEATURE_SECTIONS.find((s) => s.id === sectionId);
            if (!section) return null;
            return (
              <AccordionItem key={section.id} value={section.id} className="border-slate-100">
                <AccordionTrigger className="py-2.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400 hover:no-underline hover:text-slate-600 [&[data-state=open]]:text-slate-600">
                  {CARD_SECTION_TITLES[sectionId]}
                </AccordionTrigger>
                <AccordionContent className="pb-2 pt-0">
                  <ul className="space-y-1">
                    {section.features.map((feature) => (
                      <FeatureRow key={feature.id} feature={feature} planId={planId} />
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
}

/** Pricing tab — Intern / Associate / Professional plan comparison cards. */
export function SiteGluePlanPricingPanel({
  state,
  className,
  onSelectPlan,
  isBuilding = false,
}: SiteGluePlanPricingPanelProps) {
  const effectiveState = state ?? createEmptyConsultantState({ step: "website" });
  const selectedPlanId = selectedPlanFromState(effectiveState);

  if (isBuilding) {
    return <BuildingCompanyProfileProgress className={className} />;
  }

  return (
    <div className={cn("h-full overflow-y-auto p-4 sm:p-5", className)}>
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#4f81bd]">
          Select a Plan
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Sign up free for Intern, or buy Associate / Professional to continue onboarding.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {DISPLAY_PLANS.map((planId) => (
          <PlanEmployeeCard
            key={planId}
            planId={planId}
            selected={selectedPlanId != null && selectedPlanId === planId}
            onSelect={onSelectPlan ? () => onSelectPlan(planId) : undefined}
          />
        ))}
      </div>
    </div>
  );
}
